import { InertiaEntryContext } from '@/components/inertia-app-providers';
import { needsDocumentNavigation, type InertiaEntry } from '@/lib/app-entry';
import { Link } from '@inertiajs/react';
import { useContext, type AnchorHTMLAttributes, type ComponentProps, type ReactNode } from 'react';

type LinkProps = ComponentProps<typeof Link>;
type EntryLinkProps = LinkProps & {
    forceDocumentNavigation?: boolean;
};

/** Inertia-only <Link> props that must not leak onto a plain <a>. */
const INERTIA_ONLY_PROPS = new Set([
    'as',
    'data',
    'method',
    'headers',
    'preserveScroll',
    'preserveState',
    'replace',
    'only',
    'except',
    'async',
    'prefetch',
    'cacheFor',
    'cacheTags',
    'queryStringArrayFormat',
    'onCancelToken',
    'onBefore',
    'onStart',
    'onProgress',
    'onFinish',
    'onCancel',
    'onSuccess',
    'onError',
]);

export function useInertiaEntry(): InertiaEntry {
    return useContext(InertiaEntryContext);
}

export function EntryLink({ href, className, children, forceDocumentNavigation = false, ...props }: EntryLinkProps): ReactNode {
    const entry = useInertiaEntry();
    const url = typeof href === 'string' ? href : String(href);

    if (forceDocumentNavigation || needsDocumentNavigation(url, entry)) {
        // Keep target, rel, aria-*, onClick, etc. working on the anchor fallback.
        const anchorProps = Object.fromEntries(
            Object.entries(props).filter(([key]) => !INERTIA_ONLY_PROPS.has(key)),
        ) as AnchorHTMLAttributes<HTMLAnchorElement>;

        return (
            <a href={url} className={className} {...anchorProps}>
                {children}
            </a>
        );
    }

    return (
        <Link href={href} className={className} {...props}>
            {children}
        </Link>
    );
}
